import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@boltcrm/module-sdk/web';

export interface SessionData {
  /** `timezone`: the user's IANA timezone (`core.users.timezone`), kept in step with the browser's. */
  user: { id: string; email: string; name: string; timezone: string };
  /** What this login must do before using the company (security rules, ADR-062), and whether 2FA is on. */
  security?: {
    twoFactorEnabled: boolean;
    passwordChangeRequired: boolean;
    twoFactorSetupRequired: boolean;
    accessRestricted: 'ip' | 'time' | null;
  };
  /** `defaultCurrency`: the company's currency (core.companies), the default for new money values. */
  companies: { id: string; name: string; defaultCurrency: string }[];
  activeCompanyId: string | null;
  access: {
    accountSettingsAccess: boolean;
    appAdmin: string[];
    apps: string[];
    permissions: string[];
  } | null;
  /**
   * The enabled modules, leaving out those whose company feature switch is off (ADR-047): the shell shows only
   * these modules' nav, quick add, slots and routes.
   */
  modules: string[];
  /** Every company feature switch and whether it's on (e.g. `{ products: true }`). */
  features: Record<string, boolean>;
  /**
   * Whether this login may add a business (ADR-101): in invite mode only the instance owner; the profile menu's
   * Add business and onboarding's company form follow it.
   */
  canCreateCompany: boolean;
  /** The person who set up this install (ADR-100). */
  instanceOwner?: boolean;
  /** The product name (the server's `APP_NAME`, ADR-860). */
  appName?: string;
}

/** The signed-in user, companies and effective access (`GET /api/v1/session`). Shared by the shell and modules. */
export const sessionQuery = {
  queryKey: ['session'] as const,
  queryFn: async (): Promise<SessionData | null> => {
    try {
      return (await apiFetch<{ data: SessionData }>('/api/v1/session')).data;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  },
  staleTime: 60_000,
};

export const useSession = () => useQuery(sessionQuery);

/** Permission check for hiding controls (the API enforces it too, security.md §3.2). */
export function useCan(): (permission: string) => boolean {
  const { data } = useSession();
  const set = new Set(data?.access?.permissions ?? []);
  return (permission) => set.has(permission);
}

export const useCurrentUser = () => useSession().data?.user;

/** Whether a module is enabled and not switched off for the company (ADR-047). */
export function useModuleActive(): (moduleId: string) => boolean {
  const { data } = useSession();
  const set = new Set(data?.modules ?? []);
  return (moduleId) => set.has(moduleId);
}

/** The active company (name and default currency). */
export function useActiveCompany() {
  const { data } = useSession();
  return data?.companies.find((c) => c.id === data.activeCompanyId);
}
